import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { WarningIcon } from '../icons/WarningIcon';
import { Tube } from './layout/Tube';

const ErrorMessage = styled.div(
  ({ theme }) => css`
    color: ${theme.text.error};
    font-weight: 600;
  `
);

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  padding: 0.25rem;
`;

const Input = styled.input(
  ({ theme }) => css`
    width: 100%;
    padding: 0.75rem;
    background: ${theme.paper.default};
    outline: none;
    color: ${theme.text.default};
    border: none;
    border-radius: 0.5rem;
    font-size: 1rem;
  `
);

const TextArea = styled.textarea(
  ({ theme }) => css`
    width: 100%;
    padding: 0.75rem;
    background: ${theme.paper.default};
    outline: none;
    color: ${theme.text.default};
    border: none;
    border-radius: 0.5rem;
    font-size: 1rem;
    resize: none;
    font-family: Jost;
  `
);

const Paragraph = styled.p`
  margin: 0;
  padding: 0.25rem;
`;

interface FeedbackFormProps {
  setIsSubmitting(state: boolean): void;
  onClose(): void;
  formId: string;
}

export function FeedbackForm({
  setIsSubmitting,
  formId,
  onClose,
}: FeedbackFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [error, setError] = useState<string | undefined>(undefined);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(undefined);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: '51c8615d-2f1e-4aa6-8df0-ec2d708d9765',
          ...formData,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setFormData({ name: '', email: '', message: '' });
        onClose();
      } else {
        setError('There was an error sending your message. Please try again.');
      }
    } catch (error) {
      setError('Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  function handleTextAreaChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  }

  return (
    <Tube gap="1rem" width="100%">
      <Paragraph>
        We're always looking for ways to improve the game experience.
        <br />
        If you notice any issues with the site, have ideas for new features, or
        think we may have made a mistake then we'd love to hear from you. Use
        the form below to send us your feedback.
      </Paragraph>
      <Form onSubmit={handleSubmit} id={formId}>
        <Tube gap="0.5rem">
          <label htmlFor="name">Name:</label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            autoComplete="off"
          />
        </Tube>

        <Tube gap="0.5rem">
          <label htmlFor="email">Email:</label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            autoComplete="off"
          />
        </Tube>

        <Tube gap="0.5rem">
          <label htmlFor="message">Message:</label>
          <TextArea
            name="message"
            value={formData.message}
            onChange={handleTextAreaChange}
            required
            rows={5}
            autoComplete="off"
          />
        </Tube>
      </Form>
      {error && (
        <Tube direction="row" align="center" gap="0.5rem">
          <WarningIcon />
          <ErrorMessage>{error}</ErrorMessage>
        </Tube>
      )}
    </Tube>
  );
}
